import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { requestAngelNumber, type AngelResult } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Angel Numbers — user types a repeating number they keep seeing
 * (111, 444, 11:11, etc.) and gets a meaning + cue. Free, viral.
 *
 * UX: optional textbox + quick-tap chips for the most common ones.
 * Most users will just tap a chip, sub-100ms response (template hit).
 */

const POPULAR = ['111', '222', '333', '444', '555', '777', '888', '999', '1111', '11:11', '12:12', '22:22'];

export default function AngelNumber() {
  const { t } = useTranslation();
  const { tg, haptic } = useTelegram();

  const [input, setInput] = useState('');
  const [result, setResult] = useState<AngelResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async (numberOverride?: string) => {
    const number = (numberOverride ?? input).trim();
    if (!number || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await requestAngelNumber(number);
      setResult(r);
      haptic?.notificationOccurred('success');
    } catch (e) {
      setError((e as Error).message);
      haptic?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setInput('');
    setError(null);
  };

  const share = () => {
    if (!result) return;
    const text =
      `✦ ${result.number} ✦\n${result.meaning}\n\n${t('angel.share_cta')}`;
    if (tg?.openTelegramLink) {
      const url = 'https://t.me/share/url'
        + `?url=${encodeURIComponent(result.share_url)}`
        + `&text=${encodeURIComponent(text)}`;
      tg.openTelegramLink(url);
    } else {
      navigator.clipboard?.writeText(`${text}\n${result.share_url}`).catch(() => {});
    }
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        🔮 {t('angel.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('angel.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <OrnateFrame className="card-glow mb-4">
              <label className="block">
                <span className="mb-2 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('angel.input_label')}
                </span>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('angel.placeholder')}
                  maxLength={12}
                  className="w-full rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 text-center
                             font-serif text-xl text-gold-100 outline-none
                             tracking-widest transition
                             placeholder:text-gold-300/30 focus:border-gold-400"
                />
              </label>
            </OrnateFrame>

            <button
              type="button"
              onClick={() => ask()}
              disabled={!input.trim() || busy}
              className="btn-primary mb-5 w-full disabled:opacity-40"
            >
              {busy ? t('angel.reading') : t('angel.interpret')}
            </button>

            <p className="mb-2 text-center text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('angel.popular')}
            </p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {POPULAR.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => { setInput(n); ask(n); }}
                  disabled={busy}
                  className="rounded-full border border-gold-500/30 px-3 py-1
                             font-serif text-sm text-gold-300 transition
                             hover:border-gold-400 hover:text-gold-100
                             disabled:opacity-40"
                >
                  {n}
                </button>
              ))}
            </div>

            {error && (
              <p className="mt-3 text-center text-xs text-rose-400">{error}</p>
            )}
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Big number with sparkle aura */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 14 }}
              className="mx-auto flex h-32 w-32 items-center justify-center
                         rounded-full border-2 border-gold-400/60
                         bg-gradient-to-br from-gold-500/20 to-night-800
                         font-serif text-4xl tracking-widest text-gold-200"
              style={{ filter: 'drop-shadow(0 0 24px rgba(232,195,86,0.45))' }}
            >
              {result.number}
            </motion.div>

            <OrnateFrame className="card-glow">
              <h3 className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
                {t('angel.what_it_means')}
              </h3>
              <RichText
                text={result.meaning}
                className="font-serif italic leading-relaxed text-gold-200"
              />

              {result.cue && (
                <>
                  <h3 className="mt-4 mb-2 text-[10px] uppercase tracking-widest text-gold-500/70">
                    {t('angel.cue')}
                  </h3>
                  <RichText
                    text={result.cue}
                    className="font-serif italic leading-relaxed text-gold-200"
                  />
                </>
              )}
            </OrnateFrame>

            <div className="space-y-2">
              <button type="button" onClick={share} className="btn-ghost w-full">
                ✦ {t('angel.share')}
              </button>
              <button type="button" onClick={reset} className="btn-primary w-full">
                {t('angel.another_number')}
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
